import React, { useState } from 'react'

import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react'

import { DeviceType } from '@/graphql/generated/schemas'

import { getDeviceTypeMessage } from '../delete-device/utils/utils'
import { EditAlarmForm } from '../edit-alarm/components/EditAlarmForm'
import { EditCameraForm } from '../edit-camera/components/EditCameraForm'
import { EditDoorForm } from '../edit-door/components/EditDoorForm'
import { EditDeviceFieldsI } from '../types/types'
import { AssociateDevices } from './AssociateDevices'
import { DeviceModalHeader } from './DeviceModalHeader'
import { NoCameraAuthenticationAlert } from './NoCameraAuthenticationAlert'

interface EditDeviceModalIProps {
  onClose: () => void
  isOpen: boolean
  deviceData: EditDeviceFieldsI
}

type LoadingStates = {
  [key in DeviceType]?: boolean
}

export const EditDeviceModal = ({
  onClose,
  isOpen,
  deviceData,
}: EditDeviceModalIProps) => {
  const id = deviceData?.id
  const type = deviceData?.type
  const device = getDeviceTypeMessage(type)

  const [shouldRequireAuthentication, setShouldRequireAuthentication] =
    useState(true)
  const handleRequiresAuthenticationSwitch = (isChecked: boolean): void => {
    setShouldRequireAuthentication(isChecked)
  }
  const shouldShowNoCameraAuthenticationAlert =
    !shouldRequireAuthentication && type === DeviceType.Camera

  // Sync the loading states of the various sections
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({})

  const isAnySectionLoading = Object.values(loadingStates).some(Boolean)

  const updateLoadingState = (loadingState: boolean, device: DeviceType) => {
    setLoadingStates((prev) => ({ ...prev, [device]: loadingState }))
  }

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      scrollBehavior='inside'
      size='full'
    >
      <ModalOverlay />
      <ModalContent bg='#f9f9f9' m='6' rounded='20px'>
        <ModalCloseButton color='#000' opacity={0.4} />
        <DeviceModalHeader title={`Edit ${device}`} />
        <ModalBody
          id='editDeviceModalBody'
          overflow='auto'
          pb='6'
          pt='0'
          px='6'
        >
          <>
            {shouldShowNoCameraAuthenticationAlert && (
              <NoCameraAuthenticationAlert
                shouldRequireAuthentication={shouldRequireAuthentication}
              />
            )}
            {type === DeviceType.Alarm && (
              <EditAlarmForm
                deviceId={id}
                onLoadingChange={(loading) =>
                  updateLoadingState(loading, DeviceType.Alarm)
                }
              />
            )}
            {type === DeviceType.Camera && (
              <EditCameraForm
                deviceId={id}
                onLoadingChange={(loading) =>
                  updateLoadingState(loading, DeviceType.Camera)
                }
                onRequiresAuthenticationChange={
                  handleRequiresAuthenticationSwitch
                }
                shouldRequireAuthentication={shouldRequireAuthentication}
              />
            )}
            {type === DeviceType.Door && (
              <EditDoorForm
                deviceId={id}
                onLoadingChange={(loading) =>
                  updateLoadingState(loading, DeviceType.Door)
                }
              />
            )}

            {/* Add new Associate UI */}
            <AssociateDevices
              deviceData={deviceData}
              isOtherSectionLoading={isAnySectionLoading}
            />
          </>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
